import React, { Fragment, memo, useMemo } from 'react';

import { InputAdornment as MaterialInputAdornment } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import { ItemLabelPlacement } from 'constants/ui';

const InputAdornment = ({
  position = ItemLabelPlacement.END,
  adornmentIcon: AdornmentIcon,
  withDivider = false,
  withStylesOverride = false,
  dataTestId,
  component: Wrapper = Fragment,
  children,
  ...wrapperProps
}) => {
  const theme = useTheme();
  const overrideSx = useMemo(() => {
    if (!withStylesOverride) {
      return undefined;
    }

    const orientationX = position === ItemLabelPlacement.END ? 'left' : 'right';
    const orientationXCapitalized = position === ItemLabelPlacement.END ? 'Left' : 'Right';

    return {
      display: 'flex',
      position: 'relative',
      alignSelf: 'stretch',
      right: -14,
      left: -14,
      height: 'auto',
      maxHeight: 'none',
      [orientationX]: null,
      [`margin${orientationXCapitalized}`]: 0,
      [`border${orientationXCapitalized}`]: withDivider
        ? `1px solid ${theme.palette.divider}`
        : null,
    };
  }, [withStylesOverride, position, withDivider, theme]);

  return (
    <MaterialInputAdornment data-testid={dataTestId} position={position} sx={overrideSx}>
      <Wrapper {...wrapperProps}>
        {AdornmentIcon ? <AdornmentIcon size={IconSize[20]} /> : children}
      </Wrapper>
    </MaterialInputAdornment>
  );
};

InputAdornment.propTypes = {
  position: PropTypes.string,
  adornmentIcon: PropTypes.elementType,
  dataTestId: PropTypes.string,
  component: PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.oneOf([Fragment])]),
  withDivider: PropTypes.bool,
  withStylesOverride: PropTypes.bool,
};

export default memo(InputAdornment);
